import { Vue, Component } from 'vue-property-decorator';
import { DEFAULT_SCOPES } from '@/utils/constants';
import { checkScope } from '@/auth/checkToken';
import {
  GetIdTokenClaimsOptions,
  Auth0Client,
  LogoutOptions,
  GetTokenSilentlyOptions,
  GetTokenWithPopupOptions,
  RedirectLoginOptions,
  PopupLoginOptions,
  IdToken,
} from '@auth0/auth0-spa-js';
import { User } from '@/models/User';

export type Auth0Options = {
  domain: string,
  clientId: string,
  audience?: string,
  [key: string]: string | undefined,
}

export type RedirectCallback = (appState?: any) => void;

@Component
export class VueAuth extends Vue {
  loading = true;
  isAuthenticated?= false;
  user?: User;
  auth0Client?: Auth0Client;
  popupOpen = false;
  error?: Error;

  async getUser(): Promise<User> {
    return new User(await this.auth0Client?.getUser());
  }

  async loginWithPopup(options?: PopupLoginOptions): Promise<void> {
    this.popupOpen = true;

    try {
      await this.auth0Client?.loginWithPopup(options);
      this.user = await this.getUser();
      this.isAuthenticated = await this.auth0Client?.isAuthenticated();
      this.error = undefined;
    } catch (err) {
      this.error = err;
    } finally {
      this.popupOpen = false;
    }
  }

  async loginWithRedirect(options?: RedirectLoginOptions): Promise<void> {
    return this.auth0Client?.loginWithRedirect(options);
  }

  async getIdTokenClaims(options?: GetIdTokenClaimsOptions): Promise<IdToken | undefined> {
    return this.auth0Client?.getIdTokenClaims(options);
  }

  async getTokenSilently(options?: GetTokenSilentlyOptions): Promise<any> {
    return this.auth0Client?.getTokenSilently(options);
  }

  async getTokenWithPopup(options?: GetTokenWithPopupOptions): Promise<string | undefined> {
    return this.auth0Client?.getTokenWithPopup(options);
  }

  logout(options?: LogoutOptions): void {
    return this.auth0Client?.logout(options);
  }

  async init(onRedirectCallback: RedirectCallback, redirectUri: string, auth0Options: Auth0Options): Promise<void> {
    this.auth0Client = new Auth0Client({
      domain: auth0Options.domain,
      client_id: auth0Options.clientId,
      redirect_uri: redirectUri,
      useRefreshTokens: false, // unable to be used with different audiences
      legacySameSiteCookie: false,
      advancedOptions: {
        defaultScope: DEFAULT_SCOPES,
      },
    });

    this.isAuthenticated = false;
    this.loading = false;

    try {
      if (window.location.search.includes('code=') && window.location.search.includes('state=')) {
        const { appState } = await this.auth0Client.handleRedirectCallback() ?? { appState: undefined };
        this.error = undefined;
        this.isAuthenticated = await this.auth0Client.isAuthenticated();
        this.user = await this.getUser();

        onRedirectCallback(appState);
      }
    } catch (err) {
      this.error = err;
    }
  }

  async userHasScope(scope: string, audience: string): Promise<boolean> {
    if (this.isAuthenticated) {
      const token = await this.getTokenSilently({ audience });
      return checkScope(token, scope);
    }
    return false;
  }

  async userHasSurelockScope(scope: string): Promise<boolean> {
    return this.userHasScope(scope, 'https://surelock.cam.simplisafe.com/');
  }

  async userHasBlockbusterScope(scope: string): Promise<boolean> {
    return this.userHasScope(scope, 'https://blockbuster.cam.simplisafe.com/');
  }
}
